import { useState, useRef, useEffect } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  LayoutDashboard, Car, Users, Wallet, BarChart3,
  Tag, MessageSquareWarning, Settings, ChevronLeft,
  Bell, Search, LogOut, Map, UserCheck, Percent, Shield, ShieldOff, Clock, Key,
  Globe, CreditCard, Database, UserCog, User, Megaphone, Truck
} from 'lucide-react'
import { cn, formatCountdown } from '../../lib/utils'
import { useAuthStore } from '../../stores/authStore'
import { usePrefetchCriticalData, usePrefetchOnHover, useRealtimeNotifications } from '../../hooks'
import { notificationApi } from '../../services/api'

// Mapping des routes vers les types de prefetch
const prefetchMap: Record<string, 'users' | 'rides' | 'drivers' | 'finance' | 'claims' | 'roles' | 'zones'> = {
  '/rides': 'rides',
  '/drivers': 'drivers',
  '/users': 'users',
  '/finance': 'finance',
  '/claims': 'claims',
  '/roles': 'roles',
  '/zones': 'zones',
}

const navigation = [
  { label: 'Dashboard',      path: '/',             icon: LayoutDashboard, permission: 'dashboard.view' },
  { label: 'Courses',        path: '/rides',        icon: Car, permission: 'rides.view' },
  { label: 'Chauffeurs',     path: '/drivers',      icon: UserCheck, permission: 'drivers.view' },
  // Clients / Staff partagent la permission users.view : le superadmin
  // restreint gère les comptes back-office (Staff) mais pas la clientèle.
  { label: 'Clients',        path: '/users',        icon: Users, permission: 'users.view', excludeRoles: ['superadmin'] },
  { label: 'Staff & Admin',  path: '/staff',        icon: UserCog, permission: 'users.view' },
  { label: 'Finances',       path: '/finance',      icon: Wallet, permission: 'finance.view' },
  { label: 'Tarification',   path: '/tariffs',      icon: Map, permission: 'tariffs.view' },
  // Catalogue dynamique des types de véhicules : alimente la réservation,
  // les profils chauffeurs et les tarifs (slugs de référence).
  { label: 'Types de véhicules', path: '/vehicle-types', icon: Truck, permission: 'vehicle_types.view' },
  { label: 'Zones',          path: '/zones',        icon: Globe, permission: 'zones.view' },
  { label: 'Abonnements',    path: '/subscriptions', icon: CreditCard, permission: 'subscriptions.view' },
  { label: 'Codes promo',    path: '/promos',       icon: Percent, permission: 'promos.view' },
  // Bandeaux d'information affichés dans les applications client & chauffeur.
  { label: 'Flash infos',    path: '/flash-infos',  icon: Megaphone, permission: 'flash_infos.view' },
  { label: 'Réclamations',   path: '/claims',       icon: MessageSquareWarning, permission: 'claims.view' },
  { label: 'Rapports',       path: '/reports',      icon: BarChart3, permission: 'reports.view' },
  { label: 'Paramètres',     path: '/settings',     icon: Settings, roles: ['admin'] },
  { label: 'Notifications',  path: '/notifications', icon: Bell, permission: 'notifications.view' },
  { label: 'Journal audit',  path: '/audit',        icon: Database, permission: 'audit.view' },
  { label: 'Rôles',          path: '/roles',        icon: Shield, permission: 'roles.view' },
  { label: 'Permissions',    path: '/permissions',  icon: Key, permission: 'permissions.view' },
]

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { hasPermission, hasRole, logout, logoutAllSessions, user, tokenExpiresAt } = useAuthStore()
  
  // Précharger les données critiques au montage
  usePrefetchCriticalData()
  
  // Hook pour prefetch au hover
  const prefetchOnHover = usePrefetchOnHover()

  // Fermer le menu au clic en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Badge de notifications en direct : toute notification reçue par le membre du
  // staff (`notification.sent` sur son canal personnel, `client.{id}`) fait
  // tomber le compteur, quelle que soit la page ouverte. Le sondage de 30 s
  // ci-dessous reste un filet de sécurité si le WebSocket est indisponible.
  const queryClient = useQueryClient()

  useRealtimeNotifications(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
  })

  // Récupérer le nombre de notifications non lues de l'utilisateur connecté.
  // Le sondage n'est activé que si l'utilisateur peut réellement consulter
  // les notifications (sinon : appel refusé côté backend, inutile).
  const canViewNotifications = hasPermission('notifications.view')
  const { data: unreadResponse } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: async () => {
      try {
        const response = await notificationApi.unreadCount()
        return response.data
      } catch {
        return null
      }
    },
    enabled: canViewNotifications,
    refetchInterval: 30000, // Rafraîchir toutes les 30 secondes
  })

  const notifCount = unreadResponse?.data?.unread_count ?? 0

  // Filtrer les éléments de navigation selon les permissions réellement
  // accordées par le backend (le superadmin restreint ne voit que ses domaines).
  const filteredNavigation = navigation.filter(item => {
    if ('roles' in item && Array.isArray(item.roles)) {
      return item.roles.some((role) => hasRole(role))
    }
    if ('excludeRoles' in item && Array.isArray(item.excludeRoles) && item.excludeRoles.some((role) => hasRole(role))) {
      return false
    }
    if (!item.permission) return true
    return hasPermission(item.permission)
  })

  return (
    <div className="flex h-screen bg-gray-50 font-['Poppins',sans-serif]">
      {/* ─── Sidebar ──────────────────────────────────────────── */}
      <aside
        className={cn(
          'flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-in-out shrink-0',
          collapsed ? 'w-16' : 'w-60'
        )}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 h-16 border-b border-gray-100">
          <img 
            src="/logo.png" 
            alt="GoFlash" 
            className="w-10 h-10 rounded-lg object-contain bg-white p-1 shrink-0"
          />
          {!collapsed && (
            <span className="font-bold text-gray-900 text-lg tracking-tight">GoFlash</span>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            title={collapsed ? 'Étendre le menu' : 'Réduire le menu'}
          >
            <ChevronLeft
              className={cn('w-4 h-4 text-gray-400 transition-transform', collapsed && 'rotate-180')}
            />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto scrollbar-modern">
          {filteredNavigation.map(({ label, path, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              onMouseEnter={() => {
                // Précharger les données au survol du lien
                const prefetchType = prefetchMap[path]
                if (prefetchType) {
                  prefetchOnHover(prefetchType)
                }
              }}
              className={({ isActive }) => cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all group',
                isActive
                  ? 'bg-amber-50 text-amber-600'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
              )}
            >
              {({ isActive }) => (
                <>
                  <Icon className={cn('w-4.5 h-4.5 shrink-0', isActive ? 'text-amber-500' : 'text-gray-400 group-hover:text-gray-600')} size={18} />
                  {!collapsed && <span>{label}</span>}
                  {/* Active indicator */}
                  {isActive && !collapsed && (
                    <span className="ml-auto w-1.5 h-1.5 bg-amber-400 rounded-full" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className={cn(
          'border-t border-gray-100 p-3 flex items-center gap-3',
          collapsed ? 'justify-center' : ''
        )}>
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
            <span className="text-amber-700 font-semibold text-xs">
              {user?.name?.slice(0, 2).toUpperCase() || 'AD'}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email || user?.phone}</p>
            </div>
          )}
          {!collapsed && (
            <button 
              onClick={() => logout()}
              className="p-1 rounded-lg hover:bg-gray-100"
              title="Se déconnecter"
            >
              <LogOut className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </aside>

      {/* ─── Main Content ──────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center gap-4 px-6 shrink-0">
          {/* Search */}
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 flex-1 max-w-sm">
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              type="text"
              placeholder="Rechercher courses, clients..."
              className="bg-transparent text-sm text-gray-600 placeholder-gray-400 outline-none flex-1"
            />
            <kbd className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono hidden sm:block">⌘K</kbd>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {/* Real-time indicator */}
            <div className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-full border border-green-200">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              Live
            </div>

            {/* Notifications (masquées si l'utilisateur n'a pas la permission) */}
            {canViewNotifications && (
              <button
                onClick={() => navigate('/notifications')}
                className="relative p-2 hover:bg-gray-100 rounded-xl transition-colors group"
                title="Voir les notifications"
              >
                <Bell className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
                {notifCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                    {notifCount > 99 ? '99+' : notifCount}
                  </span>
                )}
              </button>
            )}

            {/* Avatar with Profile Menu */}
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="w-8 h-8 bg-amber-400 rounded-xl flex items-center justify-center cursor-pointer hover:bg-amber-500 transition-colors group"
                title="Profil utilisateur"
              >
                <span className="text-gray-900 font-bold text-xs group-hover:text-gray-950">
                  {user?.name?.slice(0, 2).toUpperCase() || 'AD'}
                </span>
              </button>

              {/* Profile Menu Dropdown */}
              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{user?.name || 'Admin'}</p>
                    <p className="text-xs text-gray-500">{user?.email || user?.phone}</p>
                  </div>
                  <button
                    onClick={() => {
                      navigate('/profile')
                      setShowProfileMenu(false)
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    Mon profil
                  </button>

                  {/* Session : le jeton expire (7 jours par défaut) et le
                      backend en révoque un nouveau à chaque rotation. */}
                  <div className="px-4 py-2 border-t border-gray-100">
                    <p className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      Session : {tokenExpiresAt ? formatCountdown(tokenExpiresAt) : 'inconnue'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (confirm('Déconnecter toutes les sessions, y compris sur vos autres appareils ?')) {
                        void logoutAllSessions()
                      }
                      setShowProfileMenu(false)
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
                  >
                    <ShieldOff className="w-4 h-4" />
                    Déconnecter toutes les sessions
                  </button>
                  <button
                    onClick={() => {
                      logout()
                      setShowProfileMenu(false)
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Déconnexion
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 scrollbar-overlay">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
